import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpEvent, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/product.models';
import { ApiService } from './api.service';

export interface AppReleaseDto {
  id: string;
  version: string;
  channel: string;
  minSupportedVersion: string | null;
  isMandatory: boolean;
  releaseNotes: string | null;
  feedUrl: string;
  setupUrl: string | null;
  totalSizeBytes: number;
  uploadedFiles: string | null;
  isActive: boolean;
  /** Faol qatorlar ichida versiyasi eng yuqori bo'lgani — mijozlar shuni oladi. */
  isLatest: boolean;
  publishedAt: string;
}

/** Bitta fayl uchun imzolangan yuklash manzili (backend beradi). */
export interface ReleaseUploadTarget {
  fileName: string;
  uploadUrl: string;
  contentType: string;
}

export interface ReleaseUploadUrlsInput {
  version: string;
  channel: string;
  minSupportedVersion?: string | null;
  files: string[];
}

export interface ConfirmReleaseInput {
  version: string;
  channel: string;
  minSupportedVersion?: string | null;
  isMandatory: boolean;
  releaseNotes?: string | null;
  files: string[];
}

export interface DeleteReleaseResult {
  /** R2'dan haqiqatan o'chirilgan fayllar. */
  deletedFiles: string[];
  /**
   * Boshqa chiqarilishlar ham foydalanayotgani uchun omborda QOLDIRILGAN fayllar
   * (manifest, Setup.exe kabi nomi qat'iy bo'lganlar).
   */
  keptFiles: string[];
}

@Injectable({ providedIn: 'root' })
export class AppReleasesService extends ApiService {
  private readonly endpoint = `${environment.apiUrl}/api/SuperAdmin/app-releases`;

  /**
   * Interceptorlarsiz HttpClient — faqat R2'ga to'g'ridan-to'g'ri yuklash uchun.
   *
   * Oddiy `this.http` ishlatilsa `authInterceptor` so'rovga `Authorization`
   * header'ini qo'shadi. Imzolangan manzilda ruxsat allaqachon URL ichida
   * bo'ladi va qo'shimcha header imzoni buzadi — R2 so'rovni rad etadi.
   * `api-error.interceptor` ham bu yerda ortiqcha: xatoni komponent o'zi ko'rsatadi.
   */
  private readonly storageHttp = new HttpClient(inject(HttpBackend));

  getAll(channel?: string): Observable<ApiResponse<AppReleaseDto[]>> {
    const params = channel ? new HttpParams().set('channel', channel) : undefined;
    return this.http.get<ApiResponse<AppReleaseDto[]>>(this.endpoint, { params });
  }

  /**
   * 1-qadam: imzolangan yuklash manzillarini oladi. Bu chaqiruv hech narsa
   * saqlamaydi — u faqat qoidalarni tekshiradi (versiya formati, manifest
   * borligi, dublikat versiya) va ruxsat beradi.
   */
  requestUploadUrls(input: ReleaseUploadUrlsInput): Observable<ApiResponse<ReleaseUploadTarget[]>> {
    return this.http.post<ApiResponse<ReleaseUploadTarget[]>>(
      `${this.endpoint}/upload-urls`,
      input,
    );
  }

  /**
   * 2-qadam: faylni TO'G'RIDAN-TO'G'RI R2'ga qo'yadi — api.miyzan.uz yo'lda
   * qatnashmaydi, shuning uchun nginx va Cloudflare hajm chegaralari ta'sir
   * qilmaydi.
   */
  uploadToStorage(target: ReleaseUploadTarget, file: File): Observable<HttpEvent<string>> {
    return this.storageHttp.put(target.uploadUrl, file, {
      headers: new HttpHeaders({ 'Content-Type': target.contentType }),
      reportProgress: true,
      observe: 'events',
      responseType: 'text',
    });
  }

  /**
   * 3-qadam: chiqarilishni rasmiylashtiradi. Backend har faylning omborda
   * borligini tekshirib, keyin baza qatorini yozadi — kassalar aynan shu
   * qatordan yangilanish borligini biladi.
   */
  confirm(input: ConfirmReleaseInput): Observable<ApiResponse<{ id: string }>> {
    return this.http.post<ApiResponse<{ id: string }>>(`${this.endpoint}/confirm`, input);
  }

  setActive(id: string, isActive: boolean): Observable<ApiResponse<unknown>> {
    const params = new HttpParams().set('isActive', isActive);
    return this.http.patch<ApiResponse<unknown>>(`${this.endpoint}/${id}/active`, null, { params });
  }

  /**
   * Chiqarilishni butunlay o'chiradi — qatorni ham, paketlarni R2'dan ham.
   * Qaytarib bo'lmaydi; nofaol qilish uchun `setActive(id, false)` bor.
   */
  delete(id: string): Observable<ApiResponse<DeleteReleaseResult>> {
    return this.http.delete<ApiResponse<DeleteReleaseResult>>(`${this.endpoint}/${id}`);
  }
}
